if (!customElements.get('limitless-gallery')) {
  customElements.define('limitless-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.index = 0;
      this.requestId = 0;
      this.pendingImages = new WeakMap();
      this.slides = [...this.querySelectorAll('[data-photo]')];
      this.thumbs = [...this.querySelectorAll('[data-show-photo]')];
      this.lightbox = this.querySelector('dialog');
      this.stage = this.querySelector('.ll-gallery-stage');
      if (!this.slides.length || !this.stage) return;
      this.classList.add('ll-gallery--native');
      this.slides.forEach((slide, i) => {
        slide.hidden = false;
        slide.querySelector('.ll-v8-open')?.setAttribute('tabindex', i === 0 ? '0' : '-1');
        slide.setAttribute('aria-hidden', String(i !== 0));
        const thumb = this.thumbs[i]?.querySelector('img');
        const button = slide.querySelector('.ll-v8-open');
        if (thumb && button) button.style.backgroundImage = `url("${thumb.currentSrc || thumb.src}")`;
      });
      this.addEventListener('click', event => {
        const thumb = event.target.closest('[data-show-photo]');
        const step = event.target.closest('[data-gallery-step]');
        if (thumb) this.show(Number(thumb.dataset.showPhoto));
        else if (step) this.show(this.index + Number(step.dataset.galleryStep));
        else if (event.target.closest('.ll-v8-open')) {
          this.updateLightbox();
          this.lightbox.showModal();
        } else if (event.target.closest('.ll-v8-close') || event.target === this.lightbox) this.lightbox.close();
      });
      this.addEventListener('keydown', event => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        }
      });
      // Native horizontal scrolling owns touch gestures: no touchend/click double handling.
      this.stage.addEventListener('pointerdown', () => {
        this.requestId++;
        this.navigating = false;
        this.removeAttribute('aria-busy');
      }, {passive:true});
      this.stage.addEventListener('scroll', () => {
        if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = 0;
          this.syncFromScroll();
        });
        clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(() => this.settle(), 160);
      }, {passive:true});
      this.stage.addEventListener('scrollend', () => this.settle());
      this.onResize = () => this.stage.scrollTo({left:this.index * this.stage.clientWidth, behavior:'instant'});
      window.addEventListener('resize', this.onResize);
      this.onBlockSelect = event => {
        const slide = event.target.closest('.ll-gallery-slide');
        if (slide && this.contains(slide)) this.show(Number(slide.dataset.photo));
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      const hero = this.slides[0].querySelector('img');
      if (hero?.complete && hero.naturalWidth) this.warmAdjacent();
      else hero?.addEventListener('load', () => this.warmAdjacent(), {once:true});
    }
    disconnectedCallback() {
      this.requestId++;
      clearTimeout(this.settleTimer);
      cancelAnimationFrame(this.scrollFrame);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }
    prepare(index) {
      const image = this.slides[index]?.querySelector('img');
      if (!image) return Promise.resolve(false);
      if (this.pendingImages.has(image)) return this.pendingImages.get(image);
      image.loading = 'eager';
      image.decoding = 'async';
      const ready = image.decode ? image.decode().then(() => true, () => image.complete && image.naturalWidth > 0)
        : new Promise(resolve => {
          if (image.complete) resolve(image.naturalWidth > 0);
          else { image.addEventListener('load', () => resolve(true), {once:true}); image.addEventListener('error', () => resolve(false), {once:true}); }
        });
      this.pendingImages.set(image, ready);
      ready.then(ok => { if (!ok) this.pendingImages.delete(image); });
      return ready;
    }
    warmAdjacent() {
      for (const offset of [-1, 1, 2]) {
        const index = this.index + offset;
        if (index >= 0 && index < this.slides.length) this.prepare(index);
      }
    }
    syncFromScroll() {
      if (!this.stage.clientWidth) return;
      const index = Math.max(0, Math.min(this.slides.length - 1, Math.round(this.stage.scrollLeft / this.stage.clientWidth)));
      if (index === this.index) return;
      this.index = index;
      this.slides.forEach((slide, i) => { slide.querySelector('.ll-v8-open')?.setAttribute('tabindex', i === index ? '0' : '-1'); slide.setAttribute('aria-hidden', String(i !== index)); });
      this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === index)));
      this.querySelectorAll('[data-gallery-count]').forEach(count => count.textContent = `${index + 1} / ${this.slides.length}`);
      const selected = this.thumbs[index];
      if (selected) {
        const rail = selected.parentElement;
        rail.scrollTo({left:Math.max(0,selected.offsetLeft-rail.offsetLeft-(rail.clientWidth-selected.clientWidth)/2),behavior:'instant'});
      }
      if (this.lightbox?.open) this.updateLightbox();
      this.warmAdjacent();
    }
    settle() {
      clearTimeout(this.settleTimer);
      this.syncFromScroll();
      this.navigating = false;
      this.removeAttribute('aria-busy');
    }
    async show(index) {
      // Do not accumulate swipe/tap requests while a photo is loading or snapping.
      if (this.navigating || !this.slides.length) return;
      const target = Math.max(0, Math.min(this.slides.length - 1, index));
      if (target === this.index) return;
      this.navigating = true;
      const request = ++this.requestId;
      this.setAttribute('aria-busy', 'true');
      const ready = await this.prepare(target);
      if (request !== this.requestId || !this.isConnected) return;
      if (!ready) { this.navigating = false; this.removeAttribute('aria-busy'); return; }
      this.stage.scrollTo({left:target * this.stage.clientWidth,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), 250);
    }
    updateLightbox() {
      const source = this.slides[this.index].querySelector('img');
      const enlarged = this.lightbox.querySelector('img');
      enlarged.src = source.dataset.zoomSrc || source.currentSrc || source.src;
      enlarged.alt = source.alt;
    }
  });
}
